import { useMemo, useState } from 'react';
import { CalendarBlank, CalendarCheck, CaretLeft, CaretRight, Clock, MapPin, Plus } from '@phosphor-icons/react';
import { ImmoButton, InnenKarte, KartenTitel, LeerZustand } from '../../../../../components/immobilien/ui';
import { useIslandActions } from '../../../../../components/island/IslandProvider';
import { heuteIso } from '../../../../../lib/immobilien/format';
import { KATEGORIE_INFO, inTagenText, monatsRaster, tageBis, terminKategorie, terminTag } from '../../../../../lib/immobilien/termine';
import type { TerminZeile } from '../../../../../lib/immobilien/useObjektDetail';
import { TerminFormularKarte } from '../../gemeinsam/TerminFormularKarte';
import { useObjektSeite } from '../ObjektSeitenKontext';

const MONATE = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];
const MON_KURZ = ['JAN', 'FEB', 'MÄR', 'APR', 'MAI', 'JUN', 'JUL', 'AUG', 'SEP', 'OKT', 'NOV', 'DEZ'];
const ZEIT = new Intl.DateTimeFormat('de-DE', { hour: '2-digit', minute: '2-digit' });

function zeitText(t: TerminZeile): string {
  if (t.ganztaegig) return 'ganztägig';
  const d = new Date(t.start_datum);
  return Number.isNaN(d.getTime()) ? '' : `${ZEIT.format(d)} Uhr`;
}

/** Panel „Termine“: Monatskalender des Objekts, nächster Termin, Liste; Termin anlegen und „Im Kalender zeigen“. */
export function TerminePanel() {
  const { daten, neuLaden } = useObjektSeite();
  const { onSearchNavigate } = useIslandActions();
  const heute = heuteIso();
  const [jahr, setJahr] = useState(Number(heute.slice(0, 4)));
  const [monat, setMonat] = useState(Number(heute.slice(5, 7)));
  const [gewaehlt, setGewaehlt] = useState<string | null>(null);

  const termine = useMemo(() => daten.termine
    .filter(t => (t.status ?? '') !== 'abgesagt')
    .map(t => ({ t, tag: terminTag(t.start_datum, t.ganztaegig), kat: terminKategorie(t) }))
    .sort((a, b) => a.t.start_datum.localeCompare(b.t.start_datum)), [daten.termine]);
  const proTag = useMemo(() => {
    const m = new Map<string, typeof termine>();
    for (const x of termine) m.set(x.tag, [...(m.get(x.tag) ?? []), x]);
    return m;
  }, [termine]);
  const kommende = termine.filter(x => x.tag >= heute);
  const naechster = kommende[0];
  const vergangene = termine.filter(x => x.tag < heute).reverse().slice(0, 5);
  const teamMembers = daten.zustaendigOptionen.filter(z => z.typ === 'mitarbeiter').map(z => ({ id: z.id, full_name: z.name }));

  // Termin anlegen im neuen Design (gleiches Formular, Variante „immo“) direkt im Panel
  const [neu, setNeu] = useState<{ datum: string | null } | null>(null);
  const anlegen = (datum?: string) => setNeu({ datum: datum ?? null });
  const imKalender = (id: string) => onSearchNavigate?.('/admin/kalender', id, 'termin');
  const blaettern = (d: number) => {
    let m = monat + d; let j = jahr;
    if (m < 1) { m = 12; j -= 1; } if (m > 12) { m = 1; j += 1; }
    setMonat(m); setJahr(j);
  };

  const liste = gewaehlt ? (proTag.get(gewaehlt) ?? []) : kommende.slice(0, 8);
  const rundKnopf = { width: 34, height: 34, borderRadius: '50%', border: 'none', background: 'var(--fill2)', color: 'var(--ink)' } as const;

  return (
    <>
      <div className="flex items-center justify-between flex-wrap" style={{ gap: 10 }}>
        <span style={{ fontSize: 18, fontWeight: 800, letterSpacing: '-0.01em' }}>Kalender</span>
        <ImmoButton art="akzent" groesse={40} icon={Plus} onClick={() => anlegen(gewaehlt ?? undefined)}>Termin anlegen</ImmoButton>
      </div>
      {neu && (
        <TerminFormularKarte termin={null} prefillDate={neu.datum} prefillObjektId={daten.objekt.id} teamMembers={teamMembers}
          onAbbrechen={() => setNeu(null)} onGespeichert={() => { setNeu(null); neuLaden('termine'); }} />
      )}
      <div className="flex flex-wrap items-start" style={{ gap: 14 }}>
        <InnenKarte style={{ flex: '1 1 320px', maxWidth: 360 }} radius={24}>
          <div className="flex items-center justify-between">
            <span style={{ fontSize: 17, fontWeight: 800 }}>{MONATE[monat - 1]} {jahr}</span>
            <div className="flex" style={{ gap: 6 }}>
              <button type="button" aria-label="Vorheriger Monat" onClick={() => blaettern(-1)} className="flex items-center justify-center cursor-pointer" style={rundKnopf}><CaretLeft weight="bold" size={13} /></button>
              <button type="button" aria-label="Nächster Monat" onClick={() => blaettern(1)} className="flex items-center justify-center cursor-pointer" style={rundKnopf}><CaretRight weight="bold" size={13} /></button>
            </div>
          </div>
          <div className="grid" role="grid" aria-label="Monatskalender" style={{ gridTemplateColumns: 'repeat(7,minmax(0,1fr))', gap: 4 }}>
            {['M', 'D', 'M', 'D', 'F', 'S', 'S'].map((w, i) => <span key={i} className="text-center" style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink3)', padding: '4px 0' }}>{w}</span>)}
            {monatsRaster(jahr, monat).flat().map(t => {
              const eintraege = proTag.get(t.iso) ?? [];
              const istHeute = t.iso === heute;
              const an = gewaehlt === t.iso;
              return (
                <button key={t.iso} type="button" onClick={() => setGewaehlt(an ? null : t.iso)} aria-pressed={an}
                  aria-label={`${t.tag}. ${eintraege.length ? `${eintraege.length} Termin(e)` : 'keine Termine'}`}
                  className="flex flex-col items-center justify-center cursor-pointer"
                  style={{ height: 40, borderRadius: 11, border: 'none', gap: 2, fontSize: 13, fontWeight: istHeute || eintraege.length ? 800 : 500,
                    background: istHeute ? 'var(--acc-fill)' : an ? 'var(--acc-soft)' : eintraege.length ? 'var(--fill2)' : 'transparent',
                    color: istHeute ? 'var(--acc-ink)' : t.imMonat ? 'var(--ink)' : 'var(--ink3)', boxShadow: an ? 'inset 0 0 0 2px var(--acc)' : 'none' }}>
                  {t.tag}
                  {eintraege.length > 0 && (
                    <span className="flex" style={{ gap: 2 }}>
                      {eintraege.slice(0, 3).map((e, i) => <span key={i} style={{ width: 5, height: 5, borderRadius: '50%', background: istHeute ? 'var(--acc-ink)' : KATEGORIE_INFO[e.kat].farbe }} />)}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
          <div className="flex flex-wrap" style={{ gap: '6px 12px', paddingTop: 10, borderTop: '1px solid var(--line)' }}>
            {(['wartung', 'vermietung', 'versammlung', 'ablesung'] as const).map(k => (
              <span key={k} className="flex items-center" style={{ gap: 5, fontSize: 11, fontWeight: 600, color: 'var(--ink2)' }}>
                <span style={{ width: 7, height: 7, borderRadius: '50%', background: KATEGORIE_INFO[k].farbe }} />{KATEGORIE_INFO[k].label}
              </span>
            ))}
          </div>
        </InnenKarte>

        <div className="flex flex-col min-w-0" style={{ flex: '1.4 1 340px', gap: 12 }}>
          {naechster && !gewaehlt && (
            <div className="immo-akzent flex items-center flex-wrap" style={{ borderRadius: 24, padding: 18, gap: 16 }}>
              <span className="flex flex-col items-center justify-center shrink-0" style={{ width: 62, height: 66, borderRadius: 16, background: 'var(--acc-ink-fill)' }}>
                <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.05em' }}>{MON_KURZ[Number(naechster.tag.slice(5, 7)) - 1]}</span>
                <span className="immo-zahl" style={{ fontSize: 26, fontWeight: 800, lineHeight: 1 }}>{Number(naechster.tag.slice(8, 10))}</span>
              </span>
              <div className="flex flex-col flex-1 min-w-0" style={{ gap: 3 }}>
                <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--acc-ink2)' }}>
                  Nächster Termin · {inTagenText(tageBis(naechster.tag, heute))}
                </span>
                <span className="truncate" style={{ fontSize: 20, fontWeight: 800 }}>{naechster.t.titel || 'Termin'}</span>
                <span style={{ fontSize: 12, color: 'var(--acc-ink2)' }}>{zeitText(naechster.t)}{naechster.t.ort ? ` · ${naechster.t.ort}` : ''}</span>
              </div>
              <button type="button" onClick={() => imKalender(naechster.t.id)} className="flex items-center cursor-pointer"
                style={{ height: 36, padding: '0 14px', borderRadius: 18, border: 'none', gap: 6, fontSize: 13, fontWeight: 700, background: 'var(--acc-ink-fill)', color: 'var(--acc-ink)' }}>
                <CalendarBlank weight="bold" size={14} />Im Kalender zeigen
              </button>
            </div>
          )}

          <InnenKarte padding={8} gap={2}>
            <div style={{ padding: '8px 10px' }}>
              <KartenTitel rechts={gewaehlt ? undefined : `${kommende.length} anstehend`}>
                {gewaehlt ? `Termine am ${gewaehlt.slice(8, 10)}.${gewaehlt.slice(5, 7)}.` : 'Anstehende Termine'}
              </KartenTitel>
            </div>
            {liste.length === 0 && (
              <div className="flex flex-col items-start" style={{ padding: '6px 10px 12px', gap: 8 }}>
                <span style={{ fontSize: 13, color: 'var(--ink3)' }}>{gewaehlt ? 'An diesem Tag ist nichts geplant.' : 'Keine anstehenden Termine.'}</span>
                {gewaehlt && <ImmoButton icon={Plus} onClick={() => anlegen(gewaehlt)}>Termin an diesem Tag</ImmoButton>}
              </div>
            )}
            {liste.map(({ t, tag, kat }) => (
              <button key={t.id} type="button" onClick={() => imKalender(t.id)} title="Im Kalender zeigen"
                className="immo-hover-flaeche flex items-center text-left cursor-pointer" style={{ gap: 12, padding: 10, borderRadius: 14, border: 'none', background: 'transparent', color: 'var(--ink)' }}>
                <span className="flex flex-col items-center justify-center shrink-0" style={{ width: 46, height: 48, borderRadius: 13, background: KATEGORIE_INFO[kat].flaeche }}>
                  <span style={{ fontSize: 9, fontWeight: 800, color: KATEGORIE_INFO[kat].farbe }}>{MON_KURZ[Number(tag.slice(5, 7)) - 1]}</span>
                  <span className="immo-zahl" style={{ fontSize: 17, fontWeight: 800, lineHeight: 1 }}>{tag.slice(8, 10)}</span>
                </span>
                <span className="flex flex-col flex-1 min-w-0" style={{ gap: 2 }}>
                  <span className="truncate" style={{ fontSize: 14, fontWeight: 700 }}>{t.titel || 'Termin'}</span>
                  <span className="flex items-center flex-wrap" style={{ gap: 8, fontSize: 12, color: 'var(--ink3)' }}>
                    <span style={{ color: KATEGORIE_INFO[kat].farbe, fontWeight: 700 }}>{KATEGORIE_INFO[kat].label}</span>
                    {zeitText(t) && <span className="flex items-center" style={{ gap: 3 }}><Clock size={12} />{zeitText(t)}</span>}
                    {t.ort && <span className="flex items-center truncate" style={{ gap: 3 }}><MapPin size={12} />{t.ort}</span>}
                  </span>
                </span>
                <CalendarCheck size={18} color="var(--ink3)" />
              </button>
            ))}
          </InnenKarte>

          {!gewaehlt && vergangene.length > 0 && (
            <InnenKarte padding={8} gap={2}>
              <div style={{ padding: '8px 10px' }}><KartenTitel>Zuletzt</KartenTitel></div>
              {vergangene.map(({ t, tag, kat }) => (
                <button key={t.id} type="button" onClick={() => imKalender(t.id)} className="immo-hover-flaeche flex items-center text-left cursor-pointer"
                  style={{ gap: 10, padding: '8px 10px', borderRadius: 12, border: 'none', background: 'transparent', color: 'var(--ink2)', fontSize: 13 }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: KATEGORIE_INFO[kat].farbe, flexShrink: 0 }} />
                  <span className="immo-zahl" style={{ width: 82 }}>{tag.slice(8, 10)}.{tag.slice(5, 7)}.{tag.slice(0, 4)}</span>
                  <span className="truncate flex-1">{t.titel || 'Termin'}</span>
                </button>
              ))}
            </InnenKarte>
          )}
        </div>
      </div>
      {termine.length === 0 && <LeerZustand icon={CalendarBlank} titel="Noch keine Termine" text="Lege den ersten Termin für dieses Objekt an – er erscheint auch im Kalender." />}
    </>
  );
}
